/**
 * Phase 8 T-06c §21.F — Inline "Add candidate manually" row.
 *
 * Expands below the Shortlisted-tab toolbar with three 36 px inputs
 * (`Full Name` / `Email` / `LinkedIn URL`) plus `[Cancel]` + `[Add Candidate]`.
 * The row inherits the table's 36 px row height and slides down without
 * pushing the existing match list (height is reserved by the parent's
 * `AnimatePresence` so CLS stays < 0.02 per §21.H acceptance).
 *
 * This component is a **pure UI primitive** — it calls `onSubmit` with the
 * validated payload and lets the parent decide whether to:
 *   - POST to a candidate service (Phase-8 scope bans new backend writes),
 *   - merge locally into the thread's shortlist,
 *   - or emit a toast.
 */

import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useToast } from '@shared/hooks/useToast';

export interface ManualCandidateInput {
  fullName: string;
  email: string;
  linkedin: string;
}

interface AddCandidateInlineRowProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSubmit: (payload: ManualCandidateInput) => void | Promise<void>;
  busy?: boolean;
}

const EMPTY: ManualCandidateInput = {
  fullName: '',
  email: '',
  linkedin: '',
};

function isValidEmail(v: string): boolean {
  if (!v) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function isValidUrl(v: string): boolean {
  if (!v) return true;
  try {
    const normalised = v.startsWith('http') ? v : `https://${v}`;
    const url = new URL(normalised);
    return url.hostname.length > 0;
  } catch {
    return false;
  }
}

export function AddCandidateInlineRow({
  open,
  onOpen,
  onClose,
  onSubmit,
  busy = false,
}: AddCandidateInlineRowProps) {
  const [draft, setDraft] = useState<ManualCandidateInput>(EMPTY);
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const { toast, showToast } = useToast();

  useEffect(() => {
    if (open) {
      setDraft(EMPTY);
      window.setTimeout(() => firstInputRef.current?.focus(), 30);
    }
  }, [open]);

  const emailOk = isValidEmail(draft.email);
  const urlOk = isValidUrl(draft.linkedin);
  const canSubmit =
    draft.fullName.trim().length > 0 && emailOk && urlOk && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await onSubmit({
        fullName: draft.fullName.trim(),
        email: draft.email.trim(),
        linkedin: draft.linkedin.trim(),
      });
    } catch (e) {
      showToast({
        title: 'Could not add candidate',
        body: e instanceof Error ? e.message : 'Something went wrong. Try again.',
        variant: 'error',
      });
    }
  };

  if (!open) {
    return (
      <>
        {toast}
      <button
        type="button"
        onClick={onOpen}
        className="flex h-9 w-full items-center justify-center gap-1.5 rounded-[10px] border border-dashed px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Add candidate manually
      </button>
      </>
    );
  }

  return (
    <>
      {toast}
    <div
      role="form"
      aria-label="Add candidate manually"
      className="flex items-center gap-2 overflow-hidden rounded-[10px] border px-2"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-elevated)',
        // 36 px is the canonical row height — matches data-table rows so
        // there's no vertical shift when the row appears/disappears.
        height: 36,
      }}
    >
      <input
        ref={firstInputRef}
        value={draft.fullName}
        onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="Full name"
        aria-label="Full name"
        className="h-7 flex-[1.2] min-w-0 rounded-[6px] border bg-[var(--vscode-bg-primary)] px-2 text-[12px] outline-none"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-primary)',
        }}
      />
      <input
        value={draft.email}
        onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="Email"
        aria-label="Email"
        aria-invalid={!emailOk}
        className="h-7 flex-[1.1] min-w-0 rounded-[6px] border bg-[var(--vscode-bg-primary)] px-2 text-[12px] outline-none"
        style={{
          borderColor: emailOk
            ? 'var(--vscode-border-primary)'
            : '#EF4444',
          color: 'var(--vscode-text-primary)',
        }}
      />
      <input
        value={draft.linkedin}
        onChange={(e) => setDraft({ ...draft, linkedin: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="LinkedIn URL"
        aria-label="LinkedIn URL"
        aria-invalid={!urlOk}
        className="h-7 flex-[1.3] min-w-0 rounded-[6px] border bg-[var(--vscode-bg-primary)] px-2 text-[12px] outline-none"
        style={{
          borderColor: urlOk ? 'var(--vscode-border-primary)' : '#EF4444',
          color: 'var(--vscode-text-primary)',
        }}
      />
      <button
        type="button"
        onClick={onClose}
        className="inline-flex h-7 items-center rounded-[6px] border px-2 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <X className="mr-1 h-3 w-3" aria-hidden />
        Cancel
      </button>
      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        className="inline-flex h-7 items-center rounded-[6px] bg-[var(--ggj-accent,#10B981)] px-2 text-[11px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add Candidate
      </button>
    </div>
    </>
  );
}
